import { useCallback, useMemo, useRef, useState } from 'react'

import type { SettingsFormController, SettingsFormStatus } from '../types/types'

export type SettingsSaveResult = 'saved' | 'validation-error' | void

type UseSettingsFormStateOptions = {
  onDiscard?: () => void
  onSave: () => Promise<SettingsSaveResult>
}

export type SettingsFormState = SettingsFormController & {
  markDirty: () => void
  markValidationError: () => void
  reset: () => void
}

export const useSettingsFormState = ({
  onDiscard,
  onSave,
}: UseSettingsFormStateOptions): SettingsFormState => {
  const [status, setStatus] = useState<SettingsFormStatus>('clean')
  const revisionRef = useRef(0)
  const saveAttemptRef = useRef(0)
  // Hold the latest callbacks in refs so `save`/`discard` — and therefore the
  // returned controller — stay referentially stable across renders even when a
  // caller passes fresh inline callbacks. The runtime dirty-state registration
  // (`useRegisterSettingsDirtyState`) keys an effect on the controller's
  // identity, so an unstable controller would re-register (and re-render the
  // whole Settings shell) on every render. Callbacks fire on user action,
  // always through `.current`, so they still see the latest closure.
  const onDiscardRef = useRef(onDiscard)
  onDiscardRef.current = onDiscard
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave
  const hasUnsavedChanges =
    status === 'dirty' ||
    status === 'save-error' ||
    status === 'saving' ||
    status === 'validation-error'

  const discard = useCallback(() => {
    revisionRef.current += 1
    onDiscardRef.current?.()
    setStatus('clean')
  }, [])

  const markDirty = useCallback(() => {
    revisionRef.current += 1
    setStatus('dirty')
  }, [])
  const markValidationError = useCallback(() => {
    revisionRef.current += 1
    setStatus('validation-error')
  }, [])
  const reset = useCallback(() => {
    revisionRef.current += 1
    setStatus('clean')
  }, [])

  const save = useCallback(async () => {
    const revision = revisionRef.current
    const saveAttempt = saveAttemptRef.current + 1
    saveAttemptRef.current = saveAttempt
    setStatus('saving')

    try {
      const result = await onSaveRef.current()
      if (
        revision !== revisionRef.current ||
        saveAttempt !== saveAttemptRef.current
      ) {
        return false
      }

      if (result === 'validation-error') {
        setStatus('validation-error')
        return false
      }

      setStatus('saved')
      return true
    } catch {
      if (
        revision === revisionRef.current &&
        saveAttempt === saveAttemptRef.current
      ) {
        setStatus('save-error')
      }
      return false
    }
  }, [])

  return useMemo(
    () => ({
      discard,
      hasUnsavedChanges,
      markDirty,
      markValidationError,
      reset,
      save,
      status,
    }),
    [
      discard,
      hasUnsavedChanges,
      markDirty,
      markValidationError,
      reset,
      save,
      status,
    ]
  )
}
